import React, { useEffect, useState } from "react";
import axios from "axios";
import AppLayout from "../components/AppLayout";
import { Card, CardContent } from "../components/ui/card";
import {
  PieChart, Pie, Cell,
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
} from "recharts";
import {
  PawPrint, Sparkles, SunMedium, Activity, FlaskConical, ClipboardCheck, Clock,
  TrendingUp, MessageCircle, Mail,
} from "lucide-react";
import { canViewRestrictedSections } from "../utils/auth";

const COLORS = ["#9333ea", "#c084fc", "#a855f7", "#7e22ce", "#e9d5ff", "#581c87"];
const API_URL = process.env.REACT_APP_API_URL;

const StatCard = ({ title, value, icon: Icon, color, trend, loading }) => (
  <Card className="relative overflow-hidden">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{title}</p>
          <p className={`text-3xl font-bold ${color}`}>
            {loading ? (
              <span className="inline-block h-8 w-16 bg-brand-100 rounded animate-pulse" />
            ) : (
              value
            )}
          </p>
          {trend != null && (
            <div className="flex items-center gap-1 mt-1 text-xs text-emerald-600">
              <TrendingUp className="h-3 w-3" />
              <span>{trend}</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${color.replace("text-", "bg-").replace("-700", "-100").replace("-600", "-100")}`}>
          <Icon className={`h-6 w-6 ${color}`} />
        </div>
      </div>
      {/* Barra decorativa inferior */}
      <div className={`absolute bottom-0 left-0 h-1 w-full ${color.replace("text-", "bg-").replace("-700", "-300").replace("-600", "-300")}`} />
    </CardContent>
  </Card>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-brand-100 rounded-xl shadow-card px-3 py-2 text-sm">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: <strong>{p.value}</strong></p>
      ))}
    </div>
  );
};

const DashboardPage = () => {
  const [data, setData] = useState({
    totalPacientes: 0,
    totalExamenes: 0,
    totalResultados: 0,
    resultadosPendientes: 0,
    resultadosPendientesWA: 0,
    resultadosPendientesEmail: 0,
    examenesPorEspecie: [],
    pacientesPorEspecie: [],
  });
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [now, setNow] = useState(new Date());
  const token = localStorage.getItem("token");
  const canViewMetrics = canViewRestrictedSections();
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  useEffect(() => {
    if (!canViewMetrics) { setIsInitialLoading(false); return; }
    const fetchData = async () => {
      try {
        setIsInitialLoading(true);
        const res = await axios.get(`${API_URL}/dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setIsInitialLoading(false);
      }
    };
    fetchData();
  }, [canViewMetrics, token]);

  useEffect(() => {
    if (canViewMetrics) return undefined;
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, [canViewMetrics]);

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        {/* Cabecera */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {now.toLocaleDateString("es-CO", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
            </p>
          </div>
          <div className="flex items-center gap-2 bg-white border border-brand-100 rounded-xl px-4 py-2 shadow-card">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
            </span>
            <span className="text-sm text-gray-600 font-medium">Sistema operativo</span>
          </div>
        </div>

        {canViewMetrics ? (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                title="Pacientes registrados"
                value={data.totalPacientes}
                icon={PawPrint}
                color="text-brand-600"
                loading={isInitialLoading}
              />
              <StatCard
                title="Exámenes realizados"
                value={data.totalExamenes}
                icon={FlaskConical}
                color="text-emerald-600"
                loading={isInitialLoading}
              />
              <StatCard
                title="Resultados generados"
                value={data.totalResultados}
                icon={ClipboardCheck}
                color="text-sky-600"
                loading={isInitialLoading}
              />
              <StatCard
                title="Resultados pendientes"
                value={data.resultadosPendientes}
                icon={Clock}
                color="text-amber-600"
                loading={isInitialLoading}
              />
            </div>

            {/* Indicador WhatsApp */}
            {data.resultadosPendientesWA > 0 && (
              <a href="/config" className="block">
                <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-3 hover:border-emerald-400 transition-all cursor-pointer">
                  <div className="p-2 bg-emerald-100 rounded-xl">
                    <MessageCircle className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-emerald-800">
                      {data.resultadosPendientesWA} resultado{data.resultadosPendientesWA !== 1 ? "s" : ""} validado{data.resultadosPendientesWA !== 1 ? "s" : ""} pendiente{data.resultadosPendientesWA !== 1 ? "s" : ""} de envío por WhatsApp
                    </p>
                    <p className="text-xs text-emerald-600">Haz clic para ir a configuración → WhatsApp</p>
                  </div>
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500 text-white text-xs font-bold">
                    {data.resultadosPendientesWA}
                  </span>
                </div>
              </a>
            )}

            {/* Indicador Correo */}
            {data.resultadosPendientesEmail > 0 && (
              <a href="/config" className="block">
                <div className="flex items-center gap-3 bg-sky-50 border border-sky-200 rounded-2xl px-5 py-3 hover:border-sky-400 transition-all cursor-pointer">
                  <div className="p-2 bg-sky-100 rounded-xl">
                    <Mail className="h-5 w-5 text-sky-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-sky-800">
                      {data.resultadosPendientesEmail} resultado{data.resultadosPendientesEmail !== 1 ? "s" : ""} validado{data.resultadosPendientesEmail !== 1 ? "s" : ""} pendiente{data.resultadosPendientesEmail !== 1 ? "s" : ""} de envío por correo
                    </p>
                    <p className="text-xs text-sky-600">Haz clic para ir a configuración → Correo</p>
                  </div>
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-sky-500 text-white text-xs font-bold">
                    {data.resultadosPendientesEmail}
                  </span>
                </div>
              </a>
            )}

            {/* Gráficas */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <Card>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-base font-semibold text-gray-800">Exámenes por especie</h2>
                      <p className="text-xs text-gray-500">Distribución de análisis</p>
                    </div>
                    <div className="p-2 bg-brand-100 rounded-lg">
                      <Activity className="h-4 w-4 text-brand-600" />
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={data.examenesPorEspecie} barSize={32}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3e8ff" vertical={false} />
                      <XAxis dataKey="especie" tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="count" fill="url(#barGrad)" radius={[6, 6, 0, 0]} name="Exámenes" />
                      <defs>
                        <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#9333ea" />
                          <stop offset="100%" stopColor="#c084fc" />
                        </linearGradient>
                      </defs>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-base font-semibold text-gray-800">Pacientes por especie</h2>
                      <p className="text-xs text-gray-500">Distribución por tipo</p>
                    </div>
                    <div className="p-2 bg-brand-100 rounded-lg">
                      <PawPrint className="h-4 w-4 text-brand-600" />
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie
                        data={data.pacientesPorEspecie}
                        dataKey="total"
                        nameKey="especie"
                        outerRadius={90}
                        innerRadius={50}
                        paddingAngle={3}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {(data.pacientesPorEspecie || []).map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend
                        formatter={(v) => <span style={{ fontSize: 11, color: "#6b7280" }}>{v}</span>}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </>
        ) : (
          /* Vista bienvenida para usuarios sin métricas */
          <div className="space-y-4">
            {/* Banner bienvenida */}
            <div className="bg-hero-gradient rounded-2xl p-7 relative overflow-hidden">
              <div className="absolute -top-8 -right-8 w-40 h-40 bg-white/10 rounded-full" />
              <div className="absolute bottom-0 right-16 w-24 h-24 bg-white/10 rounded-full" />
              <div className="relative z-10 flex flex-col md:flex-row md:items-center gap-5">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="h-5 w-5 text-yellow-300" />
                    <span className="text-purple-200 text-sm font-medium">Bienvenido</span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-white">
                    ¡Hola, {user?.nombre || "usuario"}!
                  </h2>
                  <p className="mt-2 text-purple-200 text-sm max-w-md">
                    Qué gusto tenerte aquí. Te deseamos una jornada productiva y llena de buenos resultados.
                  </p>
                </div>
                <div className="bg-white/15 backdrop-blur-sm rounded-2xl px-6 py-4 min-w-[220px] text-center">
                  <SunMedium className="h-5 w-5 text-yellow-300 mx-auto mb-1" />
                  <p className="text-3xl font-bold text-white">
                    {now.toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" })}
                  </p>
                  <p className="text-purple-200 text-xs mt-1">
                    {now.toLocaleDateString("es-CO", { weekday: "long", day: "numeric", month: "long" })}
                  </p>
                </div>
              </div>
            </div>

            {/* Accesos rápidos */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { href: "/patients", label: "Nuevo paciente", icon: "🐾", desc: "Registrar orden" },
                { href: "/orders", label: "Ver órdenes", icon: "📋", desc: "Consultar hoy" },
                { href: "/results", label: "Resultados", icon: "🔬", desc: "Buscar paciente" },
                { href: "/invoices", label: "Facturas", icon: "🧾", desc: "Gestionar cobros" },
              ].map((item) => (
                <a key={item.href} href={item.href}>
                  <Card className="cursor-pointer transition-all duration-200 hover:-translate-y-1 hover:shadow-card-hover">
                    <CardContent className="p-4 text-center">
                      <span className="text-3xl">{item.icon}</span>
                      <p className="font-semibold text-gray-800 text-sm mt-2">{item.label}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{item.desc}</p>
                    </CardContent>
                  </Card>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default DashboardPage;
